import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Award, DollarSign, LineChart, PieChart } from 'lucide-react';
import GainsMonitorService from '../../../services/gainsMonitorService';
import { formatAnnualBenefit } from './benefitGovernance';
import type { OpsKaizenData } from './useOpsKaizenData';
import {
  CARD_TITLE_CLASS,
  CHART_GRID,
  CHART_TICK,
  KAIZEN_GAP,
  PANEL_CLASS,
  PANEL_HEADER_COMPACT,
  RECHART_MARGIN,
  ChartPlotArea,
  PremiumTooltip,
} from './opsKaizenShared';

interface OpsKaizenFinancialTabProps {
  data: OpsKaizenData;
  periodLabel: string;
}

const WATERFALL_COLORS = ['#059669', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'];

function SavingsList({
  title,
  icon: Icon,
  rows,
  emptyLabel,
  hint,
  badge,
}: {
  title: string;
  icon: React.ElementType;
  rows: { id: string; name: string; amount: number; pct: number }[];
  emptyLabel: string;
  hint: string;
  badge?: string;
}) {
  return (
    <div className={`${PANEL_CLASS} flex min-h-0 flex-col`}>
      <div className={PANEL_HEADER_COMPACT}>
        <div className="flex min-w-0 items-center gap-1.5" title={hint}>
          <Icon size={13} className="text-emerald-600" strokeWidth={2.25} />
          <span className={CARD_TITLE_CLASS}>{title}</span>
          {badge ? (
            <span className="rounded-[3px] bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[8px] font-medium uppercase tracking-wide text-[var(--text-muted)]">
              {badge}
            </span>
          ) : null}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {rows.length === 0 ? (
          <p className="px-2 py-4 text-center text-[11px] text-[var(--text-muted)]">{emptyLabel}</p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((row, idx) => (
              <li
                key={row.id}
                className="flex items-center gap-2 rounded-sm border border-[var(--border-default)]/50 bg-[var(--bg-elevated)]/30 px-2.5 py-2"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-[9px] font-bold text-emerald-700">
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-medium text-[var(--text-primary)]">{row.name}</p>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-[var(--bg-app)]">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, row.pct)}%` }} />
                  </div>
                </div>
                <span className="shrink-0 text-[11px] font-bold tabular-nums text-emerald-600">
                  {GainsMonitorService.formatCurrency(row.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function money(value: number | null): string {
  if (value == null) return '—';
  return GainsMonitorService.formatCurrency(value);
}

function moneyPerYear(value: number | null): string {
  return formatAnnualBenefit(value);
}

function MethodPanel({ data }: { data: OpsKaizenData }) {
  const informed = data.benefitLines.filter((line) => line.annualValue != null);
  const active = informed.filter((line) => line.inclusion === 'active');
  const excluded = informed.filter((line) => line.inclusion !== 'active');
  const investmentLabel = data.consolidatedRoi.investment == null
    ? 'não informado para este mesmo conjunto'
    : GainsMonitorService.formatCurrency(data.consolidatedRoi.investment);

  return (
    <div className="mt-2 max-w-3xl space-y-2 text-[11px] leading-relaxed text-[var(--text-secondary)]">
      <p>
        Impacto anual estimado ativo é a soma de kaizen_roi.annual_cost_saved dos Kaizens em monitoramento
        ou sustentados. Planejado, em implementação e implementado ficam no potencial. Regredido fica em risco.
        Cada Kaizen entra uma vez. O valor é benefício anual estimado, não caixa realizado. Campo vazio não entra na soma.
      </p>
      {active.length === 0 ? (
        <p>Nenhum benefício anual informado nesse conjunto.</p>
      ) : (
        <ul className="space-y-1">
          {active.map((line) => (
            <li key={line.kaizenId} className="flex items-baseline justify-between gap-3">
              <span>
                <span className="font-semibold text-[var(--text-primary)]">{line.title}</span>
                <span className="ml-2 text-[10px] text-[var(--text-muted)]">{line.operationalStatusLabel}</span>
              </span>
              <span className="tabular-nums">{moneyPerYear(line.annualValue)}</span>
            </li>
          ))}
          <li className="flex items-baseline justify-between gap-3 border-t border-[var(--border-default)] pt-1">
            <span className="font-semibold text-[var(--text-primary)]">Total ativo</span>
            <span className="tabular-nums font-semibold">{moneyPerYear(data.registeredAnnualSavings)}</span>
          </li>
        </ul>
      )}
      {excluded.length > 0 && (
        <ul className="space-y-1">
          {excluded.map((line) => (
            <li key={line.kaizenId} className="flex items-baseline justify-between gap-3">
              <span>
                <span className="font-semibold text-[var(--text-primary)]">{line.title}</span>
                <span className="ml-2 text-[10px] text-[var(--text-muted)]">{line.inclusionNote}</span>
              </span>
              <span className="tabular-nums">{moneyPerYear(line.annualValue)}</span>
            </li>
          ))}
        </ul>
      )}
      <p>
        ROI = benefício anual ativo / investimento real dos mesmos Kaizens × 100.
        Payback (meses) = esse investimento / (benefício anual ativo / 12).
        Investimento: {investmentLabel}. Potencial e benefício em risco não entram nesse par.
      </p>
    </div>
  );
}

export const OpsKaizenFinancialTab: React.FC<OpsKaizenFinancialTabProps> = ({ data }) => {
  const { consolidatedRoi } = data;
  const [showMethod, setShowMethod] = useState(false);
  const [showEstimates, setShowEstimates] = useState(false);
  const composition = data.benefitLines.filter((line) => line.annualValue != null);

  const waterfallChartData = useMemo(() => {
    let cumulative = 0;
    const steps = data.financialWaterfall.map((step, idx) => {
      const start = cumulative;
      cumulative += step.amount;
      return {
        name: step.label,
        start,
        amount: step.amount,
        cumulative,
        fill: WATERFALL_COLORS[idx % WATERFALL_COLORS.length],
      };
    });
    return [
      ...steps,
      {
        name: 'Total',
        start: 0,
        amount: data.totalSavings,
        cumulative: data.totalSavings,
        fill: '#047857',
      },
    ];
  }, [data.financialWaterfall, data.totalSavings]);

  return (
    <div className={`flex h-full min-h-0 flex-col gap-3 overflow-y-auto pb-2 ${KAIZEN_GAP}`}>
      <header className="shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400">
            Impacto anual estimado ativo
          </p>
          <button
            type="button"
            onClick={() => setShowMethod((open) => !open)}
            className="text-[10px] font-medium text-[var(--text-muted)] underline-offset-2 hover:text-[var(--text-primary)] hover:underline"
          >
            Como calculamos?
          </button>
        </div>
        {showMethod && (
          <MethodPanel data={data} />
        )}
        <div className="mt-1">
          <span className="text-[32px] font-bold tabular-nums leading-none text-[var(--text-primary)]">
            {data.benefitsReady ? moneyPerYear(data.registeredAnnualSavings) : '—'}
          </span>
          <p className="mt-1 text-[11px] text-[var(--text-muted)]">
            {data.registeredAnnualSavings == null && data.benefitsReady
              ? 'Não informado nos Kaizens em monitoramento ou sustentados'
              : 'R$/ano estimado. Não é ganho realizado no período.'}
          </p>
        </div>
      </header>

      <div className="grid shrink-0 grid-cols-2 gap-2">
        <div className="rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/40 px-3 py-2">
          <p className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Benefício planejado / potencial</p>
          <p className="mt-0.5 text-[13px] font-bold tabular-nums text-[var(--text-primary)]">
            {data.benefitsReady ? moneyPerYear(data.governedBenefits?.plannedAnnual ?? null) : '—'}
          </p>
          <p className="text-[9px] text-[var(--text-muted)]">Planejado, em implementação ou implementado. Fora do impacto ativo.</p>
        </div>
        <div className="rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/40 px-3 py-2">
          <p className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Benefício em risco</p>
          <p className="mt-0.5 text-[13px] font-bold tabular-nums text-[var(--text-primary)]">
            {data.benefitsReady ? moneyPerYear(data.governedBenefits?.atRiskAnnual ?? null) : '—'}
          </p>
          <p className="text-[9px] text-[var(--text-muted)]">Kaizen regredido. Fora do impacto ativo.</p>
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-2 gap-2 lg:grid-cols-3">
        <div className="rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/40 px-3 py-2">
          <p className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Investimento real</p>
          <p className="mt-0.5 text-[13px] font-bold tabular-nums text-[var(--text-primary)]">
            {money(consolidatedRoi.investment)}
          </p>
          {consolidatedRoi.investment == null && (
            <p className="text-[9px] text-[var(--text-muted)]">Investimento não informado</p>
          )}
        </div>
        <div className="rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/40 px-3 py-2">
          <p className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)]">ROI consolidado</p>
          <p
            className="mt-0.5 text-[13px] font-bold tabular-nums text-emerald-600"
            title="ROI = benefício anual ativo / investimento dos mesmos Kaizens × 100."
          >
            {consolidatedRoi.roiPct == null ? '—' : `${consolidatedRoi.roiPct.toFixed(0)}%`}
          </p>
          {consolidatedRoi.roiPct == null && (
            <p className="text-[9px] text-[var(--text-muted)]">
              {consolidatedRoi.investment == null ? 'Investimento não informado' : 'Sem benefício anual informado'}
            </p>
          )}
        </div>
        <div className="rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/40 px-3 py-2">
          <p className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Payback</p>
          <p
            className="mt-0.5 text-[13px] font-bold tabular-nums text-[var(--text-primary)]"
            title="Payback em meses = investimento dos mesmos Kaizens / (benefício anual ativo / 12)."
          >
            {consolidatedRoi.paybackMonths == null
              ? '—'
              : `${Number(consolidatedRoi.paybackMonths.toFixed(1))} meses`}
          </p>
          {consolidatedRoi.paybackMonths == null && (
            <p className="text-[9px] text-[var(--text-muted)]">
              {consolidatedRoi.investment == null ? 'Investimento não informado' : 'Sem benefício anual informado'}
            </p>
          )}
        </div>
      </div>

      <div className={`${PANEL_CLASS} shrink-0`}>
        <div className={PANEL_HEADER_COMPACT}>
          <span className={CARD_TITLE_CLASS}>Portfólio de benefícios</span>
        </div>
        <div className="overflow-x-auto">
          {!data.benefitsReady ? (
            <p className="px-3 py-4 text-[11px] text-[var(--text-muted)]">Consultando benefícios cadastrados.</p>
          ) : data.benefitLines.length === 0 ? (
            <p className="px-3 py-4 text-[11px] text-[var(--text-muted)]">Nenhum Kaizen nesta organização.</p>
          ) : (
            <table className="w-full text-left text-[11px]">
              <thead>
                <tr className="border-b border-[var(--border-default)] text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-3 py-2 font-semibold">Iniciativa</th>
                  <th className="px-3 py-2 font-semibold">Tipo operacional</th>
                  <th className="px-3 py-2 font-semibold">Benefício anual estimado</th>
                  <th className="px-3 py-2 font-semibold">Situação do Kaizen</th>
                  <th className="px-3 py-2 font-semibold">Projeto</th>
                  <th className="px-3 py-2 font-semibold">Responsável pelo Kaizen</th>
                </tr>
              </thead>
              <tbody>
                {data.benefitLines.map((line) => (
                  <tr key={line.kaizenId} className="border-b border-[var(--border-default)]/50">
                    <td className="px-3 py-2 font-medium text-[var(--text-primary)]">{line.title}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)]">{line.typeLabel}</td>
                    <td className="px-3 py-2 tabular-nums">{moneyPerYear(line.annualValue)}</td>
                    <td className="px-3 py-2">{line.operationalStatusLabel}</td>
                    <td className="px-3 py-2">{line.projectName}</td>
                    <td className="px-3 py-2">{line.responsible ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="px-3 py-2 text-[10px] text-[var(--text-muted)]">
          A situação é a do Kaizen, não uma validação financeira. Não há evidência financeira anexada. Departamento não está ligado ao benefício.
        </p>
      </div>

      <div className="grid min-h-0 shrink-0 grid-cols-1 gap-3 lg:grid-cols-2">
        <div className={`${PANEL_CLASS} flex flex-col`}>
          <div className={PANEL_HEADER_COMPACT}>
            <span className={CARD_TITLE_CLASS}>Composição por Kaizen</span>
          </div>
          <ul className="space-y-1.5 p-2">
            {composition.length === 0 ? (
              <li className="px-2 py-3 text-center text-[11px] text-[var(--text-muted)]">Nenhum valor anual gravado.</li>
            ) : composition.map((line) => (
              <li key={line.kaizenId} className="flex items-baseline justify-between gap-3 px-2 py-1">
                <span className="truncate text-[11px] font-medium text-[var(--text-primary)]">{line.title}</span>
                <span className="min-w-0 text-right">
                  <span className="block text-[10px] text-[var(--text-muted)]">{line.inclusionNote}</span>
                  <span className="tabular-nums text-[11px] font-semibold text-emerald-600">{moneyPerYear(line.annualValue)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className={`${PANEL_CLASS} flex flex-col`}>
          <div className={PANEL_HEADER_COMPACT}>
            <span className={CARD_TITLE_CLASS}>Composição por projeto</span>
          </div>
          <ul className="space-y-1.5 p-2">
            {data.benefitByProject.length === 0 ? (
              <li className="px-2 py-3 text-center text-[11px] text-[var(--text-muted)]">Nenhum valor anual ligado a projeto.</li>
            ) : data.benefitByProject.map((row) => (
              <li key={row.id} className="flex items-baseline justify-between gap-3 px-2 py-1">
                <span className="truncate text-[11px] font-medium text-[var(--text-primary)]">{row.name}</span>
                <span className="shrink-0 tabular-nums text-[11px] font-semibold text-emerald-600">
                  {moneyPerYear(row.amount)} · {row.pct.toFixed(0)}%
                </span>
              </li>
            ))}
          </ul>
          <p className="px-3 pb-2 text-[10px] text-[var(--text-muted)]">
            Departamento não entra: o Kaizen não tem departamento. O vínculo usado é kaizen_records.project_id.
          </p>
        </div>
      </div>

      <div className="rounded-sm border border-[var(--border-default)]/60 px-3 py-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Evolução</p>
        <p className="mt-1 text-[12px] text-[var(--text-secondary)]">
          Impacto anual estimado ativo {data.benefitsReady ? moneyPerYear(data.registeredAnnualSavings) : '—'}.
          Acumulado cadastrado. Não há série temporal: o valor não é ganho do mês nem caixa realizado.
        </p>
      </div>

      <div className={`${PANEL_CLASS} shrink-0`}>
        <button
          type="button"
          onClick={() => setShowEstimates((open) => !open)}
          className={`${PANEL_HEADER_COMPACT} w-full text-left`}
        >
          <div className="flex items-center gap-1.5">
            <DollarSign size={13} className="text-emerald-600" strokeWidth={2.25} />
            <span className={CARD_TITLE_CLASS}>Análises estimadas</span>
          </div>
          <span className="text-[9px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
            {showEstimates ? 'Ocultar' : 'Rateio analítico'}
          </span>
        </button>
        {showEstimates && (
          <>
        <p className="px-3 pb-2 text-[10px] leading-relaxed text-[var(--text-muted)]">
          Modelos analíticos — não compõem benefícios financeiros adicionais.
          Distribuição analítica do impacto anual estimado ativo. Não some esses valores de novo ao total.
          O potencial por projeto é estimativa independente e não entra no ROI.
        </p>
        <ChartPlotArea fill={false} hasActivity={data.totalSavings > 0} heightClass="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={waterfallChartData} margin={RECHART_MARGIN.bar}>
              <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 9 }} />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: CHART_TICK, fontSize: 9 }}
                width={56}
                tickFormatter={(v) => `R$${Math.round(Number(v) / 1000)}k`}
              />
              <Tooltip
                content={
                  <PremiumTooltip
                    formatter={(value) => ({
                      main: GainsMonitorService.formatCurrency(value),
                      sub: 'Distribuição analítica do impacto anual estimado ativo. Não é benefício adicional.',
                    })}
                  />
                }
              />
              <Bar dataKey="start" stackId="wf" fill="transparent" />
              <Bar dataKey="amount" stackId="wf" radius={[3, 3, 0, 0]}>
                {waterfallChartData.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartPlotArea>

      <div className="grid min-h-0 shrink-0 grid-cols-1 gap-3 p-2 lg:grid-cols-3">
        <div className={`${PANEL_CLASS} flex flex-col`}>
          <div className={PANEL_HEADER_COMPACT}>
            <div className="flex items-center gap-1.5">
              <Award size={13} className="text-emerald-600" strokeWidth={2.25} />
              <span className={CARD_TITLE_CLASS}>Ranking dos maiores ganhos</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {data.gainsRanking.length === 0 ? (
              <p className="px-2 py-4 text-center text-[11px] text-[var(--text-muted)]">Sem ganhos registrados</p>
            ) : (
              <ul className="space-y-1.5">
                {data.gainsRanking.map((item, idx) => (
                  <li
                    key={item.id}
                    className="flex items-start gap-2 rounded-sm border border-[var(--border-default)]/50 bg-[var(--bg-elevated)]/30 px-2.5 py-2"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-[9px] font-bold text-emerald-700">
                      {idx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[11px] font-medium text-[var(--text-primary)]">{item.label}</p>
                      {item.sublabel && (
                        <p className="truncate text-[9px] text-[var(--text-muted)]">{item.sublabel}</p>
                      )}
                    </div>
                    <span className="shrink-0 text-[11px] font-bold tabular-nums text-emerald-600">
                      {GainsMonitorService.formatCurrency(item.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <SavingsList
          title="Distribuição estimada por etapa"
          icon={PieChart}
          rows={data.savingsByArea}
          emptyLabel="Sem dados por etapa operacional"
          hint="Distribuição analítica do impacto anual estimado ativo, com pesos de WIP e SLA. Não é benefício adicional da etapa."
        />

        <SavingsList
          title="Potencial estimado por projeto"
          icon={LineChart}
          rows={data.savingsByProject}
          emptyLabel="Sem potencial cadastrado nos Kaizens"
          badge="Estimativa independente"
          hint="Estimativa calculada a partir dos dados cadastrados no Kaizen. Não compõe o impacto anual de annual_cost_saved."
        />
      </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OpsKaizenFinancialTab;
